import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash, RefreshCw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';

import { PersonalAccessToken } from '../../../types/PersonalAccessToken';

interface PersonalAccessTokensListProps {
  tokens: PersonalAccessToken[];
  onRevoke: (token: PersonalAccessToken) => void;
  onRotate: (token: PersonalAccessToken) => void;
}

const PersonalAccessTokensList: React.FC<PersonalAccessTokensListProps> = ({
  tokens,
  onRevoke,
  onRotate,
}) => {
  const { t } = useTranslation();
  const [tokenToRevoke, setTokenToRevoke] =
    useState<PersonalAccessToken | null>(null);
  const [tokenToRotate, setTokenToRotate] =
    useState<PersonalAccessToken | null>(null);

  const handleRevoke = (token: PersonalAccessToken) => {
    setTokenToRevoke(token);
  };

  const handleRotate = (token: PersonalAccessToken) => {
    setTokenToRotate(token);
  };

  const confirmRevoke = () => {
    if (tokenToRevoke) {
      onRevoke(tokenToRevoke);
      setTokenToRevoke(null);
    }
  };

  const confirmRotate = () => {
    if (tokenToRotate) {
      onRotate(tokenToRotate);
      setTokenToRotate(null);
    }
  };

  return (
    <TooltipProvider>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.tokenName',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.description',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.scopes',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.created',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.lastUsed',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.expires',
                )}
              </TableHead>
              <TableHead className="font-semibold">
                {t(
                  'settings.api.personalAccessTokensList.table.headers.action',
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tokens.map((token) => (
              <TableRow key={token._id}>
                <TableCell>
                  <div className="truncate">{token.name}</div>
                </TableCell>
                <TableCell>
                  <div className="truncate">{token.description || '-'}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {token.permissions.map((permission) => (
                      <Badge
                        key={permission}
                        variant="secondary"
                        className="text-xs font-mono"
                      >
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {new Date(token.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {token.lastUsedAt
                    ? new Date(token.lastUsedAt).toLocaleDateString()
                    : t('settings.api.personalAccessTokensList.values.never')}
                </TableCell>
                <TableCell>
                  {token.expiresAt
                    ? new Date(token.expiresAt).toLocaleDateString()
                    : t('settings.api.personalAccessTokensList.values.never')}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevoke(token)}
                          className="h-8 w-8 p-0"
                          aria-label={t(
                            'settings.api.personalAccessTokensList.ariaLabels.delete',
                          )}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          'settings.api.personalAccessTokensList.tooltips.revokeToken',
                        )}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRotate(token)}
                          className="h-8 w-8 p-0"
                          aria-label={t(
                            'settings.api.personalAccessTokensList.ariaLabels.refresh',
                          )}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          'settings.api.personalAccessTokensList.tooltips.rotateToken',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={!!tokenToRevoke}
        onOpenChange={(open) => !open && setTokenToRevoke(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('settings.api.personalAccessTokensList.dialogs.revoke.title')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.api.personalAccessTokensList.dialogs.revoke.description',
                {
                  name: tokenToRevoke?.name,
                },
              )}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenToRevoke(null)}>
              {t(
                'settings.api.personalAccessTokensList.dialogs.revoke.buttons.cancel',
              )}
            </Button>
            <Button variant="destructive" onClick={confirmRevoke}>
              {t(
                'settings.api.personalAccessTokensList.dialogs.revoke.buttons.revoke',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate Confirmation Dialog */}
      <Dialog
        open={!!tokenToRotate}
        onOpenChange={(open) => !open && setTokenToRotate(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('settings.api.personalAccessTokensList.dialogs.rotate.title')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.api.personalAccessTokensList.dialogs.rotate.description',
                {
                  name: tokenToRotate?.name,
                },
              )}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTokenToRotate(null)}>
              {t(
                'settings.api.personalAccessTokensList.dialogs.rotate.buttons.cancel',
              )}
            </Button>
            <Button variant="default" onClick={confirmRotate}>
              {t(
                'settings.api.personalAccessTokensList.dialogs.rotate.buttons.rotate',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default PersonalAccessTokensList;
